/**
 * What the subscription screen should SAY about the next date.
 *
 * A free trial is a real Stripe subscription in `trialing` status, so
 * everything about it — the plan, the amount, the period end — looks exactly
 * like a paying subscriber's. Rendering both the same way told someone on a
 * trial "your next billing date is 21 August" over a price they had not paid
 * and might never pay, which is the one moment a user most needs to know what
 * is about to happen. It is also the disclosure consumer rules care about:
 * when the trial ends, and what happens then.
 *
 * Four states, because cancelling means something different mid-trial: a
 * paying subscriber keeps what they bought until the period ends, while a
 * trialing one is really saying "don't charge me at all".
 */
export type SubscriptionBillingLine =
  /** Trial running, will convert: say when, and to what. */
  | "trial_converts"
  /** Trial running, already cancelled: say it ends and costs nothing. */
  | "trial_cancelled"
  /** Paid, cancelled: keeps Plus until the period ends. */
  | "ends_on"
  /** Paid, renewing. */
  | "next_billing";

/**
 * Stripe's `trialing` is the only status that means a trial is running.
 * Compared case-insensitively and defensively — the field is a free-form
 * string on the client, and a status we do not recognise must fall back to
 * the paid wording rather than promising someone a trial they are not on.
 */
export function isTrialing(status: string | null | undefined): boolean {
  return typeof status === "string" && status.toLowerCase() === "trialing";
}

export function subscriptionBillingLine({
  status,
  cancelAtPeriodEnd,
}: {
  status?: string | null;
  cancelAtPeriodEnd?: boolean | null;
}): SubscriptionBillingLine {
  const cancelling = cancelAtPeriodEnd === true;
  if (isTrialing(status)) {
    return cancelling ? "trial_cancelled" : "trial_converts";
  }
  return cancelling ? "ends_on" : "next_billing";
}
